import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from '@env/environment';

/** The product name every browser tab ends with. */
export const APP_NAME = 'Health4Community';

/**
 * Owns the browser-tab title. `AppTitleStrategy` calls `setTitle()` on every navigation with the
 * label it resolved for the route, so no screen has to set its own title.
 *
 * A detail page that knows something better than a static label — the record's own name — calls it
 * again once its data arrives:
 *
 * ```ts
 * private readonly pageTitle = inject(PageTitleService);
 * // …once the record has loaded
 * this.pageTitle.setTitle(user.fullName);
 * ```
 *
 * The next navigation overwrites it, so a page never has to clean up after itself.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly title = inject(Title);

  /**
   * `Attendance · Health4Community` — plus the environment name everywhere but production, so a tab
   * open against Beta is never mistaken for the live site.
   */
  format(page?: string | null): string {
    const env = environment.environmentName?.trim();
    const app = env ? `${APP_NAME} (${env})` : APP_NAME;
    const trimmed = page?.trim();
    return trimmed ? `${trimmed} · ${app}` : app;
  }

  setTitle(page?: string | null): void {
    this.title.setTitle(this.format(page));
  }
}
